// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import * as React from "react";
import { connect } from "react-redux";
import { Navbar, Nav, Collapse } from "react-bootstrap";
import { Dropdown } from "react-bootstrap";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Link, NavLink } from "react-router-dom";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { isSet } from "utils";
import { DataState, MainPanel, INavigationPanelEntry } from "types";
import { getSite, getPanels, hasLocalSettingsPage } from "guiutils";
import { ActionType, IAction, raiseAction } from "actions";
import { UserRole } from "graphql/api";
import { IState } from "states/states";
import { AppState } from "states/schedulerstates";

import { ReactComponent as Logo } from "static/logoText.svg";
import { ReactComponent as IconDashboard } from "static/navDashboard.svg";
import { ReactComponent as IconMonitor } from "static/navMonitor.svg";
import { ReactComponent as IconPower } from "static/navPower.svg";
import { ReactComponent as IconTrend } from "static/navTrend.svg";
import { ReactComponent as IconSwitching } from "static/navSwitching.svg";
import { ReactComponent as IconCharger } from "static/navCharger.svg";
import { ReactComponent as IconRfid } from "static/navRfid.svg";
import { ReactComponent as IconChargerSessionList } from "static/navChargerSessionList.svg";
import { ReactComponent as IconChargingSession } from "static/navChargingSession.svg";
import { ReactComponent as IconOcppSettings } from "static/navOcppSettings.svg";
import { ReactComponent as IconAlarm } from "static/navAlarm.svg";
import { ReactComponent as IconConnection } from "static/navConnection.svg";
import { ReactComponent as IconUser } from "static/navUser.svg";
import { ReactComponent as IconSuperUser } from "static/navSuperUser.svg";
import { ReactComponent as IconSettings } from "static/navSettings.svg";

// uses webpack definitions
declare const IS_CLOUD: boolean;

interface INavigationExtProps {
  showNavigation: boolean;
}

interface INavigationProps {
  appState: AppState;
  userName: string | undefined;
  isAdminUser: boolean;
  isRfidUser: boolean;
  siteName: string | undefined;
  showLocalSettings: boolean;
  serialNumber: string | undefined;

  isOffline: boolean;
  isBusy: boolean;
  nrPendingAlarms: number;
  panelMap: INavigationPanelEntry[];
  nrPanels: number;
  mainPanel: MainPanel | undefined;
  hasTransaction: boolean;

  navigationIsOpen: boolean;
  isDropdownOpen: {
    [panel in MainPanel]?: boolean;
  };
}

const mapStateToProps = (state: IState) => {
  const site = getSite(state);
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const isBusy =
    [
      AppState.INIT,
      AppState.LOGGED_IN,
      AppState.SITE_SELECTED,
      AppState.PARAMETER_LOADED,
      AppState.LIVE_DATA_SUBSCRIBED,
    ].includes(state.scheduler.appState) ||
    state.scheduler.trendLoadingState === DataState.SAVE_REQ;
  const { panels, nrPanels } = getPanels(state);

  const props: INavigationProps = {
    appState: state.scheduler.appState,
    userName: curUser?.name ?? state.site.user?.realName,
    isAdminUser: [UserRole.admin, UserRole.root].includes(curUser?.role ?? UserRole.viewer),
    isRfidUser: !!state.site.user?.isRfidUser,
    siteName: site?.name,
    showLocalSettings: hasLocalSettingsPage(state.controller),
    serialNumber: state.controller?.serialNumber,
    isOffline: state.scheduler.isOffline && state.scheduler.appState >= AppState.SITE_SELECTED,
    isBusy,
    nrPendingAlarms: state.data.alarms.length,
    panelMap: panels,
    nrPanels,
    mainPanel: state.router.panel,
    hasTransaction: isSet(state.router.transaction),
    navigationIsOpen: state.gui.navigationIsOpen,
    isDropdownOpen: state.gui.isDropdownOpen,
  };
  return props;
};

interface INavigationDispatch {
  onNavigationToggle: () => void;
  onDropdownToggle: (panel: MainPanel) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: INavigationDispatch = {
    onNavigationToggle: () => {
      dispatch(raiseAction(ActionType.ACT_NAVIGATION_COLLAPSE));
    },
    onDropdownToggle: (panel: MainPanel) => {
      dispatch(raiseAction(ActionType.ACT_NAVIGATION_DROPDOWN_COLLAPSE, panel));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_copyright: {
    id: "by Energie-Plan + Management GmbH",
  },
  msg_offline: {
    id: "offline",
  },
  msg_user: {
    id: "view user",
  },
  msg_site: {
    id: "view site",
  },
  msg_local_settings: {
    id: "view controller settings",
  },
});

type INavElement = {
  icon: React.ReactElement;
  mainIcon?: React.ReactElement;
  label: React.ReactElement;
  mainLabel?: React.ReactElement;
};
type NavMap = {
  [panel in MainPanel]?: INavElement;
};

export class NavigationComponent extends React.Component<
  INavigationProps & INavigationExtProps & INavigationDispatch & WrappedComponentProps & RoutedProps
> {
  private navEntries: NavMap;
  private wrapperRef: HTMLDivElement | undefined;
  private readonly PANELS_LIMIT = 6; // above this, we create sub-menus
  private readonly COLLAPSE_PANELS_LIMIT = 3; // above this, we use collapse on small screens

  constructor(props: any) {
    super(props);
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);

    // setup list of shown panels
    this.navEntries = {
      [MainPanel.DASHBOARD]: {
        icon: <IconDashboard />,
        label: (
          <span className="optional">
            <FormattedMessage id="Dashboard" />
          </span>
        ),
      },
      [MainPanel.MONITOR]: {
        icon: <IconMonitor />,
        label: <FormattedMessage id="Monitor" />,
      },
      [MainPanel.POWER]: {
        icon: <IconPower />,
        label: <FormattedMessage id="Power" />,
      },
      [MainPanel.ENERGY]: {
        icon: <IconTrend />,
        label: <FormattedMessage id="nav_Trend Data" defaultMessage="Trend Data" />,
      },
      [MainPanel.TRENDMONITOR]: {
        icon: <IconMonitor />,
        label: <FormattedMessage id="Trend Monitor" />,
      },
      [MainPanel.REPORT]: {
        icon: <IconTrend />,
        label: <FormattedMessage id="Reports" />,
      },
      [MainPanel.PUBLIC_DISPLAY]: {
        icon: <IconMonitor />,
        label: <FormattedMessage id="Public Display" />,
      },
      [MainPanel.IOS]: {
        icon: <IconSwitching />,
        label: <FormattedMessage id="Current Status" />,
        mainLabel: <FormattedMessage id="Input/Outputs" />,
      },
      [MainPanel.IO_TREND]: {
        icon: <IconSwitching />,
        label: <FormattedMessage id="IO Graph" />,
      },
      [MainPanel.CHARGERLIST]: {
        icon: <IconCharger />,
        label: <FormattedMessage id="Chargers" />,
        mainLabel: <FormattedMessage id="E-Mobility" />,
      },
      [MainPanel.ENLINE_CHARGER]: {
        icon: <IconCharger />,
        label: IS_CLOUD ? (
          <FormattedMessage id="Energy Management Chargers" />
        ) : (
          <FormattedMessage id="Chargers" />
        ),
      },
      [MainPanel.OCPPCONFIGURATION]: {
        icon: <IconOcppSettings />,
        label: <FormattedMessage id="Configuration" />,
      },
      [MainPanel.CHARGINGSESSIONLIST]: {
        icon: <IconChargerSessionList />,
        label: <FormattedMessage id="Charging Sessions" />,
      },
      [MainPanel.CHARGINGSESSION]: {
        icon: <IconChargingSession />,
        label: <FormattedMessage id="Active Session" />,
      },
      [MainPanel.RFIDLIST]: {
        icon: <IconRfid />,
        label: <FormattedMessage id="Charging Cards" />,
      },
      [MainPanel.ALARMS]: {
        icon: <IconAlarm />,
        label: <FormattedMessage id="Alarms" />,
      },
    };
  }

  public componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside.bind(this));
  }
  public componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside.bind(this));
  }

  private getMainItem(panels: INavigationPanelEntry[]) {
    let mainItem: MainPanel | undefined;
    panels.forEach((curItem) => {
      if (mainItem === undefined) {
        if (curItem.childPanels === undefined) {
          mainItem = curItem.heading;
        } else {
          mainItem = this.getMainItem(curItem.childPanels);
        }
      }
    });
    return mainItem;
  }

  private containsItem(panels: INavigationPanelEntry[], item: MainPanel | undefined): boolean {
    if (!isSet(item)) {
      return false;
    }
    return panels.some((curItem) => {
      if (curItem.heading === item) {
        return true;
      }
      if (curItem.subPanels?.includes(item)) {
        return true;
      }
      if (curItem.childPanels === undefined) {
        // no sub-structure present
        return false;
      }
      return this.containsItem(curItem.childPanels, item);
    });
  }

  public render() {
    const defaultPanel = this.getMainItem(this.props.panelMap);
    const navbarClasses = ["fixed-top"];
    if (
      this.props.appState < AppState.READY ||
      !this.props.showNavigation ||
      this.props.nrPanels <= this.COLLAPSE_PANELS_LIMIT
    ) {
      navbarClasses.push("no-menu");
    }

    return (
      <>
        <div ref={this.setWrapperRef.bind(this)}>
          <Navbar
            expand={
              this.props.showNavigation && this.props.nrPanels > this.COLLAPSE_PANELS_LIMIT
                ? "lg"
                : true
            }
            className={navbarClasses.join(" ")}
            style={this.props.navigationIsOpen ? { zIndex: 1054 } : undefined}
            variant="light"
          >
            {this.renderLogo(defaultPanel ?? MainPanel.USER)}
            <div id="sitename" className="d-none d-print-inline-block" style={{ marginTop: 5 }}>
              {this.props.siteName}
            </div>
            {this.props.appState >= AppState.READY &&
              (this.props.showNavigation ? (
                this.renderNavigationItems()
              ) : (
                <Nav>{this.getNavItem({ heading: defaultPanel ?? MainPanel.SITES }, 0)}</Nav>
              ))}
            {IS_CLOUD && this.props.appState >= AppState.LOGGED_IN && this.renderUser()}
            {!IS_CLOUD &&
              (this.props.showLocalSettings
                ? this.renderLocalSettings()
                : this.props.appState < AppState.READY && this.renderDeviceInfo())}
          </Navbar>
        </div>
        <div className={this.props.navigationIsOpen ? "modal-backdrop show" : undefined}></div>
      </>
    );
  }

  private renderLogo(defaultPanel: MainPanel | undefined) {
    const isLinked =
      this.props.appState >= AppState.READY || this.props.mainPanel === MainPanel.LOCAL_SETTINGS;
    return (
      <div
        title={this.props.intl.formatMessage(messages.msg_copyright)}
        className={
          this.props.nrPanels > this.COLLAPSE_PANELS_LIMIT
            ? "navbar-brand"
            : "navbar-brand d-none d-sm-block d-print-block"
        }
      >
        {isLinked && isSet(defaultPanel) ? (
          <Link to={`${defaultPanel}`}>
            <Logo id="logo" className={this.props.isBusy ? "animated" : undefined} />
          </Link>
        ) : (
          <Logo id="logo" className={this.props.isBusy ? "animated" : undefined} />
        )}
      </div>
    );
  }

  private getNavItems(panelList: INavigationPanelEntry[], level: number) {
    const navItems: JSX.Element[] = [];
    panelList.forEach((curEntry) => {
      if (curEntry.childPanels === undefined) {
        // entry without sub-structure
        navItems.push(this.getNavItem(curEntry, level));
      } else {
        // empty list?
        if (curEntry.childPanels.length === 0) {
          return;
        }
        // single item only or too few panels in total?
        if (curEntry.childPanels.length === 1 || this.props.nrPanels <= this.PANELS_LIMIT) {
          navItems.push(...this.getNavItems(curEntry.childPanels, level));
          return;
        }
        // multiple items; make sub-menu
        const mainPanel = curEntry.heading;
        const isActivePanel = this.containsItem([curEntry], this.props.mainPanel);
        const subItems = this.getNavItems(curEntry.childPanels, level + 1);
        const newItem = (
          <Dropdown
            as="li"
            key={curEntry.heading}
            show={this.props.isDropdownOpen[mainPanel] ? true : false}
            onToggle={() => this.props.onDropdownToggle(mainPanel)}
            className={`nav-item ${isActivePanel ? "active" : ""}`}
          >
            <Dropdown.Toggle as={Nav.Link}>
              {this.navEntries[curEntry.heading]?.mainIcon ??
                this.navEntries[curEntry.heading]?.icon}{" "}
              {this.navEntries[curEntry.heading]?.mainLabel ??
                this.navEntries[curEntry.heading]?.label}
            </Dropdown.Toggle>
            <Dropdown.Menu className="static-dropdown">{subItems}</Dropdown.Menu>
          </Dropdown>
        );
        navItems.push(newItem);
      }
    });
    return navItems;
  }

  private renderNavigationItems() {
    return (
      <>
        <Navbar.Toggle onClick={this.props.onNavigationToggle} className="d-print-none" />
        <Navbar.Collapse in={this.props.navigationIsOpen} className="d-print-none">
          <Nav as="ul">{this.getNavItems(this.props.panelMap, 0)}</Nav>
        </Navbar.Collapse>
      </>
    );
  }

  private getNavItem(panel: INavigationPanelEntry, level: number, isDisabled?: boolean) {
    let isActivePanel = this.containsItem([panel], this.props.mainPanel);
    // we need special tweaking of the Active Session panel
    if (this.props.mainPanel === MainPanel.CHARGINGSESSION && this.props.hasTransaction) {
      // if we have a transaction selected, the Active Session shall not be active
      if (panel.heading === MainPanel.CHARGINGSESSION) {
        isActivePanel = false;
      }
      if (panel.heading === MainPanel.CHARGINGSESSIONLIST) {
        isActivePanel = true;
      }
    }
    const classes = ["d-print-none"];
    if (isDisabled) {
      classes.push("disabled");
    }
    if (isActivePanel) {
      classes.push("active");
    }
    if (level === 0) {
      return (
        <Nav.Item key={panel.heading} as="li" className={classes.join(" ")}>
          <Nav.Link as={NavLink} to={`${panel.heading}`} active={isActivePanel}>
            {this.navEntries[panel.heading]!.icon} {this.navEntries[panel.heading]!.label}
          </Nav.Link>
        </Nav.Item>
      );
    }
    return (
      <Dropdown.Item
        as="button"
        key={panel.heading}
        id={`navdown_${panel.heading}`}
        className={`level-${level} ${classes.join(" ")}`}
      >
        <NavLink to={`${panel.heading}`}>{this.navEntries[panel.heading]!.label}</NavLink>
      </Dropdown.Item>
    );
  }

  private renderUser() {
    return (
      <div className="usericon d-print-none">
        {this.props.isRfidUser === false && (
          <NavLink
            className={
              "nav-item" +
              ([MainPanel.SITES, MainPanel.SITE_SETTINGS].includes(this.props.mainPanel!)
                ? " active"
                : "")
            }
            to={`${MainPanel.SITES}`}
            title={this.props.isOffline ? this.props.intl.formatMessage(messages.msg_offline) : ""}
          >
            <span title={this.props.intl.formatMessage(messages.msg_site)}>
              <IconConnection className={this.props.isOffline ? "offline" : ""} />
            </span>
            <span className="optional-userinfo ms-1">{this.props.siteName}</span>
          </NavLink>
        )}
        <NavLink
          className={
            "nav-item" +
            ([MainPanel.USER, MainPanel.USER_SETTINGS].includes(this.props.mainPanel!)
              ? " active"
              : "")
          }
          to={`${MainPanel.USER}`}
        >
          <span title={this.props.intl.formatMessage(messages.msg_user)}>
            {this.props.isAdminUser ? <IconSuperUser /> : <IconUser />}
          </span>
          <span className="optional-userinfo ms-1">{this.props.userName}</span>
        </NavLink>
      </div>
    );
  }

  private renderLocalSettings() {
    return (
      <div className="usericon d-print-none">
        <NavLink
          className={
            "nav-item" + (this.props.mainPanel === MainPanel.LOCAL_SETTINGS ? " active" : "")
          }
          to={`${MainPanel.LOCAL_SETTINGS}`}
          title={this.props.intl.formatMessage(messages.msg_local_settings)}
        >
          <IconSettings />
        </NavLink>
      </div>
    );
  }

  private renderDeviceInfo() {
    if (isSet(this.props.serialNumber)) {
      return (
        <div className="usericon d-print-none" style={{ marginTop: 16, marginRight: 15 }}>
          <FormattedMessage id="Device" />: {this.props.serialNumber}
        </div>
      );
    }
    return <></>;
  }

  private setWrapperRef(node: HTMLDivElement) {
    // set the wrapper reference
    this.wrapperRef = node;
  }

  private handleClickOutside(event: MouseEvent) {
    if (
      this.wrapperRef &&
      !this.wrapperRef.contains(event.target as Node) &&
      this.props.navigationIsOpen
    ) {
      // close navigation bar
      this.props.onNavigationToggle();
    }
  }
}

const Navigation = connect(mapStateToProps, mapDispatchToProps)(withRouter(NavigationComponent));

export default injectIntl(Navigation);
